import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "./config/apiBaseUrl";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import PageTransition from "./components/PageTransition";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import HowItWorks from "./components/HowItWorks";
import StoryTypes from "./components/StoryTypes";
import HomeDifferentStyles from "./components/HomeDifferentStyles";
import FeaturesStrip from "./components/FeaturesStrip";
import HomeCTA from "./components/HomeCTA";
import HomeSectionDivider from "./components/HomeSectionDivider";
import WizardPage from "./pages/WizardPage";
import Step1b_StoryType from "./components/Wizard/Step1b_StoryType";
import Step2_Images from "./components/Wizard/Step2_Images";
import StoryDetailsStep from "./pages/wizard/StoryDetailsStep";
import ReviewStep from "./pages/wizard/ReviewStep";
import StoryResult from "./components/Wizard/StoryResult";
import DigitalBookViewPage from "./pages/DigitalBookViewPage";
import StoryStudio from "./components/Wizard/StoryStudio";
import ImageStudio from "./components/Wizard/ImageStudio";
import CoverStudio from "./components/Wizard/CoverStudio";
import PhotoTraining from "./components/Wizard/PhotoTraining";
import CharacterAnalysis from "./components/Wizard/CharacterAnalysis";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AdminVerification from "./pages/AdminVerification";
import BookRequestSubmitted from "./pages/BookRequestSubmitted";
import CheckEmail from "./pages/CheckEmail";
import VerifyEmail from "./pages/VerifyEmail";
import AdminSubmissions from "./pages/AdminSubmissions";
import { useAuth } from "./context/AuthContext";
import { useWizard } from "./context/WizardContext";
import { useTranslation } from "./i18n/useTranslation";
import ScrollToTop from "./components/ScrollToTop";
import SiteFooter from "./components/SiteFooter";
import AccessibilityStatement from "./pages/AccessibilityStatement";
import TermsOfUse from "./pages/TermsOfUse";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AccessibilityWidget from "./components/AccessibilityWidget";
import PlatformLanguageSwitcher from "./components/PlatformLanguageSwitcher";
import {
  HOME_HERO_PORTRAIT_MEDIA,
  HOME_HERO_STAGE_BG_HEIGHT_CLASS,
} from "./constants/homeHeroStage";
import { useMediaQuery } from "./utils/useMediaQuery";
import {
  clearAdminWorkSessionStorage,
  isAdminSubmissionWorkPath,
  readAdminWorkSessionId,
} from "./utils/adminWorkSessionStorage";
import {
  HOME_MAIN_BACKGROUND_PHONE_URL,
  HOME_MAIN_BACKGROUND_URL,
  HOME_PRE_BACKGROUND_PHONE_URL,
  HOME_PRE_BACKGROUND_URL,
} from "./regularUserBackgroundPreload";

/** Opacity layer visible after load: darker `pre-background` vs bright `background.png`. */
type HomeHeroVisualPhase = "pre" | "main";

/** Fully settled time on each background before the white flash begins (next swap). */
const HOME_BG_HOLD_MS = 4000;
const WHITE_FADE_IN_MS = 900;
const WHITE_HOLD_MS = 220;
const FLASH_PREP_MS = WHITE_FADE_IN_MS + WHITE_HOLD_MS;
/** White fades out together with the incoming background layer (matches original home). */
const HOME_BG_REVEAL_FADE_MS = 4000;
/** Outgoing pre-background layer drops away quickly during reveal (matches original). */
const HOME_PRE_LAYER_OUT_MS = 260;

/** Five swaps: pre→main→pre→main→pre→main (three pre→bright transitions, ending on background.png). */
const HOME_PRE_MAIN_SWAP_SERIES_COUNT = 5;

/** Landscape hero art is nudged up; portrait phone art is anchored top (sky band for copy). */
const homeHeroBgStyle = (usePortraitHero: boolean) =>
  usePortraitHero
    ? ({ backgroundPosition: "center top" as const } satisfies React.CSSProperties)
    : ({ backgroundPositionY: "-110px" as const } satisfies React.CSSProperties);

const HomePage: React.FC = () => {
  const usePortraitHero = useMediaQuery(HOME_HERO_PORTRAIT_MEDIA);
  const preBgUrl = usePortraitHero ? HOME_PRE_BACKGROUND_PHONE_URL : HOME_PRE_BACKGROUND_URL;
  const mainBgUrl = usePortraitHero ? HOME_MAIN_BACKGROUND_PHONE_URL : HOME_MAIN_BACKGROUND_URL;
  const heroBgAlignedStyle = homeHeroBgStyle(usePortraitHero);

  const [isMainBackgroundReady, setIsMainBackgroundReady] = useState(false);
  const [bgPhase, setBgPhase] = useState<HomeHeroVisualPhase>("pre");
  const [showWhiteFlash, setShowWhiteFlash] = useState(false);
  const [revealActive, setRevealActive] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    setIsMainBackgroundReady(false);
    let pending = 2;
    const markOne = () => {
      pending -= 1;
      if (pending <= 0) setIsMainBackgroundReady(true);
    };
    const preload = (src: string) => {
      const img = new Image();
      let done = false;
      const once = () => {
        if (done) return;
        done = true;
        markOne();
      };
      img.onload = once;
      img.onerror = once;
      img.src = src;
      if (img.complete && img.naturalWidth > 0) once();
    };
    preload(preBgUrl);
    preload(mainBgUrl);
  }, [preBgUrl, mainBgUrl]);

  useEffect(() => {
    const timers: number[] = [];
    let accum = HOME_BG_HOLD_MS;

    for (let i = 0; i < HOME_PRE_MAIN_SWAP_SERIES_COUNT; i += 1) {
      const revealMain = i % 2 === 0;
      timers.push(window.setTimeout(() => setShowWhiteFlash(true), accum));
      accum += FLASH_PREP_MS;
      timers.push(
        window.setTimeout(() => {
          setBgPhase(revealMain ? "main" : "pre");
          setShowWhiteFlash(false);
          setRevealActive(true);
        }, accum)
      );
      accum += HOME_BG_REVEAL_FADE_MS;
      timers.push(window.setTimeout(() => setRevealActive(false), accum));
      accum += HOME_BG_HOLD_MS;
    }

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const isPrePhase = bgPhase === "pre";
  const isMainPhase = bgPhase === "main" && isMainBackgroundReady;

  const wantPreVisible =
    revealActive
      ? isPrePhase || (bgPhase === "main" && !isMainBackgroundReady)
      : isPrePhase || (bgPhase === "main" && !isMainBackgroundReady);

  const wantMainVisible =
    revealActive ? isMainPhase : isMainPhase;

  const preTransitionMs = revealActive
    ? isPrePhase
      ? HOME_BG_REVEAL_FADE_MS
      : HOME_PRE_LAYER_OUT_MS
    : HOME_PRE_LAYER_OUT_MS;

  const mainTransitionMs = revealActive
    ? isMainPhase
      ? HOME_BG_REVEAL_FADE_MS
      : HOME_PRE_LAYER_OUT_MS
    : HOME_PRE_LAYER_OUT_MS;

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-brandBeige">
      <div
        className={`absolute top-0 left-0 right-0 w-full bg-cover bg-no-repeat bg-center z-0 transition-opacity ease-out ${HOME_HERO_STAGE_BG_HEIGHT_CLASS} ${
          wantPreVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{
          ...heroBgAlignedStyle,
          backgroundImage: `url('${preBgUrl}')`,
          transitionDuration: `${preTransitionMs}ms`,
        }}
      />
      <div
        className={`absolute top-0 left-0 right-0 w-full bg-cover bg-no-repeat bg-center z-0 transition-opacity ease-out ${HOME_HERO_STAGE_BG_HEIGHT_CLASS} ${
          wantMainVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{
          ...heroBgAlignedStyle,
          backgroundImage: `url('${mainBgUrl}')`,
          transitionDuration: `${mainTransitionMs}ms`,
        }}
      />
      <div
        className={`absolute top-0 left-0 right-0 w-full z-0 pointer-events-none transition-opacity ease-out ${HOME_HERO_STAGE_BG_HEIGHT_CLASS} ${
          revealActive ? "opacity-0" : showWhiteFlash ? "opacity-95" : "opacity-0"
        }`}
        style={{
          backgroundColor: "#ffffff",
          transitionDuration: revealActive ? `${HOME_BG_REVEAL_FADE_MS}ms` : `${WHITE_FADE_IN_MS}ms`,
        }}
      />
      <div className="relative z-10">
        <main id="main-content" tabIndex={-1} className="min-w-0 overflow-x-hidden outline-none">
          <HeroSection />
          <HowItWorks />
          <HomeSectionDivider />
          <StoryTypes />
          <HomeSectionDivider />
          <HomeDifferentStyles />
          <HomeSectionDivider />
          <FeaturesStrip />
          <HomeSectionDivider />
          <HomeCTA />
        </main>
        <SiteFooter showAccessibilityLink variant="full" />
      </div>
    </div>
  );
};

const BLOCKED_WIZARD_PATHS = [
  "/create/story-studio",
  "/create/photo-training",
  "/create/character-analysis",
  "/create/image-studio",
  "/create/cover-studio",
];

const ProtectedRoute: React.FC = () => {
  const { token, isLoading } = useAuth();

  if (isLoading) return null;
  if (!token) return <Navigate to="/login" replace />;

  return <Outlet />;
};

const ProductionRouteGuard: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();

  const productionMode = user?.productionMode === true;
  const isAdmin = user?.isAdmin === true;

  if (productionMode && !isAdmin) {
    const path = location.pathname;
    if (path === "/create/digital-book") {
      return <Outlet />;
    }
    if (BLOCKED_WIZARD_PATHS.some((p) => path === p || path.startsWith(p + "/"))) {
      return <Navigate to="/create/order-summary" replace />;
    }
  }

  return <Outlet />;
};

const WizardGateLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { sessionId, loadSession, submissionOwnerEmail } = useWizard();
  const { t } = useTranslation();

  const [gateReady, setGateReady] = useState(false);

  const productionMode = user?.productionMode === true;
  const isAdmin = user?.isAdmin === true;
  const isProductionRegularUser = productionMode && !isAdmin;

  /** Avoid re-running gate when only reader params (e.g. ?p=) change */
  const urlSessionIdTrimmed = useMemo(
    () => new URLSearchParams(location.search).get("sessionId")?.trim() ?? "",
    [location.search]
  );
  const urlStartFreshFlag = useMemo(
    () => new URLSearchParams(location.search).get("startFresh") === "1",
    [location.search]
  );

  useEffect(() => {
    let isMounted = true;
    const startFresh = urlStartFreshFlag;
    const forcedSessionId = urlSessionIdTrimmed || "";

    const run = async () => {
      if (authLoading) return;
      if (!user) {
        setGateReady(true);
        return;
      }

      // Admin: resume the selected submission after refresh even when ?sessionId= was dropped from the URL
      // (internal navigations often omit it; falling through to currentStorySessionId would load the admin's own draft).
      if (isAdmin) {
        const fromUrl = forcedSessionId.trim() ? forcedSessionId : null;
        const fromStorage = isAdminSubmissionWorkPath(location.pathname) ? readAdminWorkSessionId() : null;
        const sid = fromUrl || fromStorage;
        if (sid) {
          if (!fromUrl && fromStorage) {
            navigate(
              { pathname: location.pathname, search: `?sessionId=${encodeURIComponent(fromStorage)}` },
              { replace: true }
            );
            return;
          }
          const result = await loadSession(sid);
          if (result.success) {
            if (isMounted) setGateReady(true);
            return;
          }
          if (!fromUrl && fromStorage && sid === fromStorage) {
            clearAdminWorkSessionStorage();
          }
          // Fall through so the admin is not stuck on a blank gate if storage or URL was stale.
        }
      }

      // Dedicated digital book reader (non-production or dev admin): allow direct session from URL
      if (location.pathname === "/create/digital-book" && !isProductionRegularUser) {
        const sid = forcedSessionId.trim() || "";
        if (sid) {
          await loadSession(sid);
          if (isMounted) setGateReady(true);
          return;
        }
        if (isMounted) {
          navigate("/create/order-summary", { replace: true });
        }
        return;
      }

      // Production regular user: always check if they already have a submitted book request
      if (isProductionRegularUser) {
        try {
          const token = sessionStorage.getItem("mysipur_auth_token");
          if (token) {
            const res = await fetch(`${API_BASE_URL}/stories/book-request`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (data?.success && data?.data) {
              const requestStatus = data.data?.submissionStatus;
              const bookRequestSessionId = data.data?.sessionId as string | undefined;
              const storyReadyForDigitalBookReview =
                requestStatus === "Your Story Is Ready" ||
                requestStatus === "Required Changes Submitted";

              const isResultPage = location.pathname === "/create/result";
              const sidQuery = forcedSessionId.trim() ? forcedSessionId : undefined;
              const sessionMatchesTheirBookRequest =
                !!bookRequestSessionId &&
                !!sidQuery &&
                sidQuery === bookRequestSessionId;

              const canOpenResult =
                isResultPage &&
                storyReadyForDigitalBookReview &&
                sessionMatchesTheirBookRequest;

              const isDigitalBookPage = location.pathname === "/create/digital-book";
              /* Same readiness as result: customers must not need “publish invite” just to open the reader in-app */
              const canOpenDigitalBook =
                isDigitalBookPage &&
                !!sidQuery &&
                !!bookRequestSessionId &&
                sidQuery === bookRequestSessionId &&
                storyReadyForDigitalBookReview;

              if (canOpenDigitalBook && sidQuery) {
                /* Best-effort: DigitalBookViewPage loads the story independently; failures must not redirect away */
                await loadSession(sidQuery);
                if (isMounted) {
                  setGateReady(true);
                }
                return;
              }

              if (canOpenResult) {
                if (isMounted) setGateReady(true);
                return;
              }

              if (isMounted) {
                navigate("/create/order-summary", { replace: true });
              }
              return;
            }
          }
        } catch {
          // Continue with normal flow on error
        }
      }

      if (startFresh) {
        clearAdminWorkSessionStorage();
        setGateReady(true);
        return;
      }

      if (sessionId) {
        if (isMounted) setGateReady(true);
        return;
      }

      const targetSessionId = user.currentStorySessionId;
      if (!targetSessionId) {
        if (isMounted) setGateReady(true);
        return;
      }

      const result = await loadSession(targetSessionId);
      if (!isMounted) return;

      if (result?.success && result.redirectPath) {
        // Production regular user: never redirect to blocked paths
        let redirectPath = result.redirectPath;
        if (isProductionRegularUser && BLOCKED_WIZARD_PATHS.some((p) => redirectPath === p || redirectPath.startsWith(p + "/"))) {
          redirectPath = "/create/order-summary";
        }
        navigate(redirectPath, { replace: true });
        if (isMounted) setGateReady(true);
        return;
      }

      setGateReady(true);
    };

    run().catch(() => {
      if (isMounted) setGateReady(true);
    });

    return () => {
      isMounted = false;
    };
  }, [
    authLoading,
    user,
    sessionId,
    loadSession,
    navigate,
    location.pathname,
    urlSessionIdTrimmed,
    urlStartFreshFlag,
    isProductionRegularUser,
    isAdmin,
  ]);

  if (authLoading || !gateReady) return null;
  const showAdminSubmissionBanner = user?.isAdmin === true && !!submissionOwnerEmail;
  return (
    <>
      {showAdminSubmissionBanner ? (
        <div
          className="sticky top-0 z-40 w-full border-b border-amber-200/80 bg-amber-50/95 px-4 py-2 text-center text-sm font-semibold text-amber-950 shadow-sm backdrop-blur-sm"
          role="status"
        >
          <span dir="ltr" className="inline-block max-w-full break-all">
            {t("wizard.adminWorkingOnBefore")}
            <strong className="font-bold text-blue-800">{submissionOwnerEmail}</strong>
            {t("wizard.adminWorkingOnAfter")}
          </span>
        </div>
      ) : null}
      <Outlet />
    </>
  );
};

/** Main site chrome: fixed marketing header only exists on the home route (see Header). */
const AppShell: React.FC = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const hideMarketingChrome =
    location.pathname === "/create/digital-book" ||
    location.pathname.endsWith("/create/digital-book") ||
    location.pathname === "/create/result";

  return (
    <div className="contents">
      <ScrollToTop />
      {isHome && <Header />}
      {!isHome && !hideMarketingChrome && <PlatformLanguageSwitcher variant="fixed" />}
      <AccessibilityWidget />
      <Routes>
        <Route element={<PageTransition />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/admin-verification" element={<AdminVerification />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<ProductionRouteGuard />}>
              <Route path="/admin/submissions" element={<AdminSubmissions />} />
              <Route path="/create/order-summary" element={<BookRequestSubmitted />} />

              {/* New "create" URLs */}
              <Route path="/create" element={<WizardGateLayout />}>
                <Route index element={<Navigate to="/create/language" replace />} />
                <Route path="language" element={<WizardPage />} />
                <Route path="story-type" element={<Step1b_StoryType />} />
                <Route path="photos-upload" element={<Step2_Images />} />
                <Route path="story-details" element={<StoryDetailsStep />} />
                <Route path="review" element={<ReviewStep />} />
                <Route path="story-studio" element={<StoryStudio />} />
                <Route path="photo-training" element={<PhotoTraining />} />
                <Route path="character-analysis" element={<CharacterAnalysis />} />
                <Route path="image-studio" element={<ImageStudio />} />
                <Route path="cover-studio" element={<CoverStudio />} />
                <Route path="result" element={<StoryResult />} />
                <Route path="digital-book" element={<DigitalBookViewPage />} />
              </Route>

              {/* Backward-compatible redirects (old wizard URLs) */}
              <Route path="/wizard" element={<Navigate to="/create/language" replace />} />
              <Route path="/wizard/step-1b" element={<Navigate to="/create/story-type" replace />} />
              <Route path="/wizard/step-2" element={<Navigate to="/create/photos-upload" replace />} />
              <Route path="/wizard/step-3" element={<Navigate to="/create/story-details" replace />} />
              <Route path="/wizard/studio" element={<Navigate to="/create/story-studio" replace />} />
              <Route path="/wizard/photo-training" element={<Navigate to="/create/photo-training" replace />} />
              <Route path="/wizard/character-analysis" element={<Navigate to="/create/character-analysis" replace />} />
              <Route path="/wizard/image-studio" element={<Navigate to="/create/image-studio" replace />} />
              <Route path="/wizard/cover-studio" element={<Navigate to="/create/cover-studio" replace />} />
              <Route path="/wizard/result" element={<Navigate to="/create/result" replace />} />
              <Route path="/book-request-submitted" element={<Navigate to="/create/order-summary" replace />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppShell />
    </Router>
  );
};

export default App;